import { initializeApp, getApps } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithRedirect, 
  signOut, 
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  onSnapshot, 
  doc, 
  setDoc, 
  deleteDoc, 
  enableIndexedDbPersistence 
} from 'firebase/firestore';
import { bulkUpsertNotes } from './db.js';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyA1U2nuHOgWqtRcLY4kS46uCul6sTiDJkg",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "flow-note-baa07.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "flow-note-baa07",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "flow-note-baa07.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "307210343996",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:307210343996:web:6b4d9196e0742be25e1539"
};

let app;
let auth;
let dbInstance;
let isConnected = false;
let currentUser = null;
let notesUnsubscribe = null;

export function initFirebase(onNotesRemoteUpdated, onStatusChanged, onAuthUpdated) {
  try {
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApps()[0];
    }
    
    auth = getAuth(app);
    setPersistence(auth, browserLocalPersistence).catch(console.warn);

    dbInstance = getFirestore(app);
    enableIndexedDbPersistence(dbInstance).catch((err) => {
      console.warn("Firestore offline persistence fallback:", err.code);
    });

    // Auth State Listener
    onAuthStateChanged(auth, (user) => {
      currentUser = user;
      if (onAuthUpdated) onAuthUpdated(user);

      if (user) {
        subscribeToUserNotes(user.uid, onNotesRemoteUpdated, onStatusChanged);
      } else {
        if (notesUnsubscribe) {
          notesUnsubscribe();
          notesUnsubscribe = null;
        }
        isConnected = false;
        if (onStatusChanged) onStatusChanged(false);
      }
    });

  } catch (err) {
    console.error("Firebase init failed:", err);
    isConnected = false;
    if (onStatusChanged) onStatusChanged(false);
  }
}

function subscribeToUserNotes(userId, onNotesRemoteUpdated, onStatusChanged) {
  if (notesUnsubscribe) notesUnsubscribe();

  const userNotesColRef = collection(dbInstance, 'users', userId, 'notes');

  notesUnsubscribe = onSnapshot(userNotesColRef, (snapshot) => {
    isConnected = true;
    if (onStatusChanged) onStatusChanged(true);

    const remoteNotes = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // The desktop app soft-deletes into an archive. Those notes are still in
      // Firestore but must not resurface here as if they were live.
      if (data.isArchived) return;
      remoteNotes.push({
        id: docSnap.id,
        content: data.content || '',
        colorTheme: data.colorTheme || 'paper',
        isPinned: data.isPinned || false,
        reminderAt: data.reminderAt || null,
        cardStates: data.cardStates || {},
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString()
      });
    });

    if (remoteNotes.length > 0) {
      bulkUpsertNotes(remoteNotes).then(() => {
        if (onNotesRemoteUpdated) onNotesRemoteUpdated(remoteNotes);
      });
    }
  }, (err) => {
    console.warn("Firebase snapshot error (using offline IndexedDB):", err);
    isConnected = false;
    if (onStatusChanged) onStatusChanged(false);
  });
}

export async function signInWithGoogle() {
  if (!auth) auth = getAuth(app);
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (err) {
    console.warn("Popup auth error:", err);
    throw err;
  }
}

export async function logoutUser() {
  if (!auth) auth = getAuth(app);
  await signOut(auth);
  currentUser = null;
}

export function getCurrentUser() {
  return currentUser || auth?.currentUser || null;
}

export async function syncNoteToFirebase(note) {
  const user = getCurrentUser();
  if (!dbInstance || !note?.id || !user) return;
  try {
    const noteRef = doc(dbInstance, 'users', user.uid, 'notes', note.id);
    await setDoc(noteRef, {
      content: note.content || '',
      colorTheme: note.colorTheme || 'paper',
      isPinned: note.isPinned || false,
      reminderAt: note.reminderAt || null,
      cardStates: note.cardStates || {},
      // Never resurrect a desktop-archived note by writing isArchived:false over it.
      // merge:true means omitting the field leaves the desktop's value untouched.
      ...(note.isArchived === undefined ? {} : { isArchived: note.isArchived }),
      createdAt: note.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: user.uid
    }, { merge: true });
  } catch (err) {
    console.warn("Firestore syncNote failed (cached locally):", err);
  }
}

export async function deleteNoteFromFirebase(noteId) {
  const user = getCurrentUser();
  if (!dbInstance || !noteId || !user) return;
  try {
    const noteRef = doc(dbInstance, 'users', user.uid, 'notes', noteId);
    await deleteDoc(noteRef);
  } catch (err) {
    console.warn("Firestore deleteNote failed:", err);
  }
}

export function getIsConnected() {
  return isConnected;
}
